"use client";

import { useRouter } from "next/navigation";
import { useCallback, useEffect, useState } from "react";
import {
  type ClockInInput,
  clockInAction,
} from "@/actions/serverActions/attendance/portal/clockInAction";
import {
  type ClockOutInput,
  clockOutAction,
} from "@/actions/serverActions/attendance/portal/clockOutAction";
import { useServerAction } from "@/hooks/useServerAction";
import { useAlert } from "@/Provider/AlertProvider/AlertProvider";

const COOLDOWN_MS = 2 * 60 * 1000;
export const COOLDOWN_KEY = "portal_clock_cooldown_until";

/** Clear the clock in/out cooldown from localStorage (e.g. on logout). */
export const clearPortalClockCooldown = () => {
  if (typeof window === "undefined") return;
  localStorage.removeItem(COOLDOWN_KEY);
};

const readCooldown = (): number | null => {
  if (typeof window === "undefined") return null;
  const raw = localStorage.getItem(COOLDOWN_KEY);
  if (!raw) return null;
  const ts = Number(raw);
  return ts > Date.now() ? ts : null;
};

const writeCooldown = (until: number) => {
  localStorage.setItem(COOLDOWN_KEY, String(until));
};

const clearCooldown = () => {
  localStorage.removeItem(COOLDOWN_KEY);
};

export const usePortalAttendanceActions = () => {
  const [isClockInLoading, setIsClockInLoading] = useState(false);
  const [isBreakLoading, setIsBreakLoading] = useState(false);
  const [isClockOutLoading, setIsClockOutLoading] = useState(false);
  const [cooldownUntil, setCooldownUntil] = useState<number | null>(
    readCooldown,
  );
  const router = useRouter();
  const { showError, showSuccess } = useAlert();

  const clockIn = useServerAction(clockInAction);
  const clockOut = useServerAction(clockOutAction);

  const isCoolingDown = cooldownUntil !== null && Date.now() < cooldownUntil;

  useEffect(() => {
    if (!cooldownUntil) return;
    const remaining = cooldownUntil - Date.now();
    if (remaining <= 0) {
      setCooldownUntil(null);
      clearCooldown();
      return;
    }
    const id = setTimeout(() => {
      setCooldownUntil(null);
      clearCooldown();
    }, remaining);
    return () => clearTimeout(id);
  }, [cooldownUntil]);

  const handleClockIn = useCallback(
    async (reason?: ClockInInput["reason"]) => {
      if (isCoolingDown) return;
      const until = Date.now() + COOLDOWN_MS;
      setCooldownUntil(until);
      writeCooldown(until);
      setIsClockInLoading(true);
      const isReturnFromBreak = reason === "return_from_break";
      try {
        const result = await clockIn({ reason });
        if (result.success) {
          showSuccess(
            isReturnFromBreak
              ? "Break ended successfully"
              : "Clocked in successfully",
          );
          router.refresh();
        } else {
          showError(
            result.message ??
              (isReturnFromBreak
                ? "Failed to end break"
                : "Failed to clock in"),
          );
        }
      } finally {
        setIsClockInLoading(false);
      }
    },
    [clockIn, isCoolingDown, router, showError, showSuccess],
  );

  const handleClockOut = useCallback(
    async (reason?: ClockOutInput["reason"]) => {
      if (isCoolingDown) return;
      const until = Date.now() + COOLDOWN_MS;
      setCooldownUntil(until);
      writeCooldown(until);
      const setLoading =
        reason === "break" ? setIsBreakLoading : setIsClockOutLoading;
      setLoading(true);
      const isBreak = reason === "break";
      try {
        const result = await clockOut({ reason });
        if (result.success) {
          showSuccess(
            isBreak ? "Break started successfully" : "Clocked out successfully",
          );
          router.refresh();
        } else {
          showError(
            result.message ??
              (isBreak ? "Failed to start break" : "Failed to clock out"),
          );
        }
      } finally {
        setLoading(false);
      }
    },
    [clockOut, isCoolingDown, router, showError, showSuccess],
  );

  return {
    isClockInLoading,
    isBreakLoading,
    isClockOutLoading,
    isCoolingDown,
    handleClockIn,
    handleClockOut,
  };
};
